import moment from "moment";
import { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useLazyViewSolarPlantAlertsAdminQuery } from "../../../../redux/api/generation/generationAPI";
import SitesErrorLogTable from "../../../../shared/components/site-error-log-table/site-error-log-table";
import IViewAdminSolarPlantAlertsRequestDTO from "../../../../shared/oversight-core/dtos/request-dtos/view-admin-solar-plant-alerts-request-dto";
import IViewAdminSolarPlantAlertsResponseDTO from "../../../../shared/oversight-core/dtos/response-dtos/view-admin-solar-plant-alerts-response-dto";
import { EAlertState } from "../../../../shared/oversight-core/enums/alert-state";
import { ESolarServiceProvider } from "../../../../shared/oversight-core/enums/solar-service-provider";
import { IAlertOptions } from "../../../../shared/oversight-core/interfaces/alert-options";
import { IGoodWeSolarPlantAlert } from "../../../../shared/oversight-core/interfaces/good-we-solar-plant-alert";
import { IGroWattSolarPlantAlert } from "../../../../shared/oversight-core/interfaces/gro-watt-solar-plant-alert";
import { INormalizedAlert } from "../../../../shared/oversight-core/interfaces/normalized-alert";
import { IPowerHubSolarPlantAlert } from "../../../../shared/oversight-core/interfaces/powerhub-solar-plant-alert";
import { IVrmSolarPlantAlert } from "../../../../shared/oversight-core/interfaces/vrm-solar-plant-alert";
import AppButton from "../../../../shared/oversight-core/ui-elements/buttons/app-button/app-button";
import ButtonWithBadge from "../../../../shared/oversight-core/ui-elements/buttons/button-with-badge/button-with-badge";
import DataTable from "../../../../shared/oversight-core/ui-elements/data-table/data-table";
import MaterialIcon from "../../../../shared/oversight-core/ui-elements/material-icon/material-icon";
import SpinnerModal from "../../../../shared/oversight-core/ui-elements/spinner/spinner";
import {
  getOpenInButtonText,
  openServiceProviderPortal,
} from "../../../../shared/oversight-core/utils/service-provider-portal-utils";
import {
  formatEpochToDate,
  getTimestampDuration,
} from "../../../../shared/oversight-core/utils/time-utils";
import translate from "../../../../shared/oversight-core/utils/translate";
import {
  countAlertFilters,
  IAlertFilterValues,
  severityLabels,
} from "../../components/alert-filter-fields/alert-filter-fields";
import SiteErrorLogFilterPanel from "./site-error-log-filter-panel/site-error-log-filter-panel";

interface IProps {
  plantId: string;
  zoneId?: string;
  serviceProvider: ESolarServiceProvider;
}
export interface ISiteErrorLogFilters extends IAlertFilterValues {
  startDate: string;
  endDate: string;
}

// A site's error log is its alert history, so status defaults to open and closed.
export const initialSiteErrorLogFilters: ISiteErrorLogFilters = {
  startDate: "",
  endDate: "",
  severity: [],
  alertState: EAlertState.ALL,
  alertName: [],
};

// Brands with their own table above; any other brand's alerts show in the common table.
const BRANDS_WITH_OWN_TABLE = [
  ESolarServiceProvider.VRM,
  ESolarServiceProvider.GOODWE,
  ESolarServiceProvider.GROWATT,
  ESolarServiceProvider.POWERHUB,
];

interface IAlertWithNormalized {
  normalized?: INormalizedAlert;
}

const formatUtc = (value?: string) =>
  value ? moment.utc(value).format("DD/MM/YY HH:mm:ss") : "-";

// The backend's open/closed reading when it is there, else what the brand's own flag says.
const isOpen = (alert: IAlertWithNormalized, fallback: boolean): boolean =>
  alert.normalized?.open ?? fallback;

const SiteErrorLog = (props: IProps) => {
  const { plantId, zoneId, serviceProvider } = props;

  const [showSiteViewDashboardFilters, setShowSiteViewDashboardFilters] =
    useState(false);
  const [vrmSolarPlantAlerts, setVrmSolarPlantAlerts] = useState<
    IVrmSolarPlantAlert[]
  >([]);
  const [goodWeSolarPlantAlerts, setGoodWeSolarPlantAlerts] = useState<
    IGoodWeSolarPlantAlert[]
  >([]);
  const [groWattSolarPlantAlerts, setGroWattSolarPlantAlerts] = useState<
    IGroWattSolarPlantAlert[]
  >([]);
  const [powerHubSolarPlantAlerts, setPowerHubSolarPlantAlerts] = useState<
    IPowerHubSolarPlantAlert[]
  >([]);
  const [otherAlerts, setOtherAlerts] = useState<IAlertWithNormalized[]>([]);
  const [alertOptions, setAlertOptions] = useState<IAlertOptions>();
  const [filters, setFilters] = useState<ISiteErrorLogFilters>({
    ...initialSiteErrorLogFilters,
  });
  const [pageSize, setPageSize] = useState(10);

  const [triggerSolarPlantAlertsAdmin, { isFetching }] =
    useLazyViewSolarPlantAlertsAdminQuery();

  useEffect(() => {
    if (plantId) {
      const filterParams: IViewAdminSolarPlantAlertsRequestDTO = {
        plantId: plantId,
        pageNumber: 0,
        pageSize: pageSize,
      };

      if (filters.startDate) {
        filterParams.start = filters.startDate;
      }

      if (filters.endDate) {
        filterParams.end = filters.endDate;
      }

      filterParams.severity = filters.severity;
      filterParams.alertState = filters.alertState;
      filterParams.alertName = filters.alertName;

      triggerSolarPlantAlertsAdmin({ ...filterParams })
        .unwrap()
        .then((res: IViewAdminSolarPlantAlertsResponseDTO) => {
          setVrmSolarPlantAlerts(res.alerts as IVrmSolarPlantAlert[]);
          setGoodWeSolarPlantAlerts(res.alerts as IGoodWeSolarPlantAlert[]);
          setGroWattSolarPlantAlerts(res.alerts as IGroWattSolarPlantAlert[]);
          setPowerHubSolarPlantAlerts(res.alerts as IPowerHubSolarPlantAlert[]);
          setOtherAlerts(res.alerts as IAlertWithNormalized[]);
          setAlertOptions(res.alertOptions);
        })
        .catch(() => {
          setVrmSolarPlantAlerts([]);
          setGoodWeSolarPlantAlerts([]);
          setGroWattSolarPlantAlerts([]);
          setPowerHubSolarPlantAlerts([]);
          setOtherAlerts([]);
        });
    }
  }, [triggerSolarPlantAlertsAdmin, plantId, filters, pageSize]);

  const updateFilters = (changes: Partial<ISiteErrorLogFilters>) => {
    setFilters((previous) => ({ ...previous, ...changes }));
  };

  const filterBadgeValue =
    (filters.startDate || filters.endDate ? 1 : 0) +
    countAlertFilters(filters, EAlertState.ALL);

  const showCommonTable = !BRANDS_WITH_OWN_TABLE.includes(serviceProvider);

  return (
    <div className="position-relative">
      <Row className="row-cols-auto justify-content-between">
        <Col>
          <AppButton
            iconName="open_in_new"
            iconWeight={700}
            text={getOpenInButtonText(serviceProvider)}
            variant="transparentWithBorder"
            className="font-size-14"
            onClick={() => openServiceProviderPortal(serviceProvider)}
          />
        </Col>
        <Col>
          <ButtonWithBadge
            badgeValue={filterBadgeValue}
            text="Filter"
            icon="filter_alt"
            onClick={() => setShowSiteViewDashboardFilters(true)}
          />
        </Col>
      </Row>
      {serviceProvider === ESolarServiceProvider.VRM &&
        vrmSolarPlantAlerts.length > 0 && (
          <div className="bg-white">
            <Row className="mt-4">
              <Col>
                <SitesErrorLogTable
                  headers={[
                    { id: 1, columnName: "Start Time" },
                    { id: 2, columnName: "Instance" },
                    { id: 3, columnName: "Device" },
                    { id: 4, columnName: "Description" },
                    { id: 5, columnName: "Cleared After" },
                  ]}
                  data={vrmSolarPlantAlerts
                    .slice(0, pageSize)
                    .map((vrmSolarPlantAlert) => {
                      return {
                        data: [
                          <>
                            {formatEpochToDate(
                              vrmSolarPlantAlert.started,
                              zoneId,
                            )}
                          </>,
                          <>{vrmSolarPlantAlert.instance}</>,
                          <>
                            {vrmSolarPlantAlert.device
                              ? vrmSolarPlantAlert.device
                              : "Unknown"}
                          </>,
                          <>
                            {vrmSolarPlantAlert.description}:&nbsp;
                            {vrmSolarPlantAlert.nameEnum}
                          </>,
                          <>
                            {vrmSolarPlantAlert.isActive === 1
                              ? `Active`
                              : vrmSolarPlantAlert.started &&
                                  vrmSolarPlantAlert.cleared
                                ? getTimestampDuration(
                                    vrmSolarPlantAlert.started,
                                    vrmSolarPlantAlert.cleared,
                                  )
                                : "-"}
                          </>,
                        ],
                        cellWidths: [150, 100, 200, 550, 150],
                        isActive: vrmSolarPlantAlert.isActive === 1,
                      };
                    })}
                />
              </Col>
            </Row>
          </div>
        )}
      {serviceProvider === ESolarServiceProvider.GOODWE &&
        goodWeSolarPlantAlerts.length > 0 && (
          <div className="bg-white">
            <Row className="mt-4">
              <Col>
                <DataTable
                  headers={[
                    { id: 1, columnName: "SN" },
                    { id: 2, columnName: "Device Type" },
                    { id: 3, columnName: "Description" },
                    { id: 4, columnName: "Fault Level" },
                    { id: 5, columnName: "Fault Classification" },
                    { id: 6, columnName: "Status" },
                    { id: 7, columnName: "Time" },
                  ]}
                  data={goodWeSolarPlantAlerts
                    .slice(0, pageSize)
                    .map((goodWeSolarPlantAlert) => {
                      return {
                        data: [
                          <>{goodWeSolarPlantAlert.devicesn}</>,
                          <>{translate(goodWeSolarPlantAlert.device_type)}</>,
                          <>{goodWeSolarPlantAlert.warningname}</>,
                          <>{goodWeSolarPlantAlert.standard_level}</>,
                          <>
                            {goodWeSolarPlantAlert.fault_classification
                              .map((i) => translate(i))
                              .join(", ")}
                          </>,
                          <>
                            <div
                              className={
                                isOpen(
                                  goodWeSolarPlantAlert,
                                  !goodWeSolarPlantAlert.status,
                                )
                                  ? `text-red`
                                  : `text-light`
                              }
                            >
                              {isOpen(
                                goodWeSolarPlantAlert,
                                !goodWeSolarPlantAlert.status,
                              )
                                ? `Happening`
                                : `Recovered`}
                            </div>
                          </>,
                          <>
                            {moment
                              .utc(goodWeSolarPlantAlert.happentime)
                              .format("DD/MM/YY HH:mm:ss")}
                          </>,
                        ],
                      };
                    })}
                />
              </Col>
            </Row>
          </div>
        )}
      {serviceProvider === ESolarServiceProvider.GROWATT &&
        groWattSolarPlantAlerts.length > 0 && (
          <div className="bg-white">
            <Row className="mt-4 px-3">
              <Col>
                <DataTable
                  headers={[
                    { id: 1, columnName: "Alarm Code" },
                    { id: 2, columnName: "Start Time" },
                    { id: 7, columnName: "End Time" },
                    { id: 3, columnName: "Description" },
                    { id: 6, columnName: "Status" },
                  ]}
                  data={groWattSolarPlantAlerts
                    .slice(0, pageSize)
                    .map((groWattSolarPlantAlert) => {
                      return {
                        data: [
                          <>{groWattSolarPlantAlert.alarmCode}</>,
                          <>
                            {groWattSolarPlantAlert.startTime
                              ? moment
                                  .utc(groWattSolarPlantAlert.startTime)
                                  .format("DD/MM/YY HH:mm:ss")
                              : "-"}
                          </>,
                          <>
                            {groWattSolarPlantAlert.endTime
                              ? moment
                                  .utc(groWattSolarPlantAlert.endTime)
                                  .format("DD/MM/YY HH:mm:ss")
                              : "-"}
                          </>,
                          <>{groWattSolarPlantAlert.alarmMessage}</>,
                          <>
                            <div
                              className={
                                isOpen(
                                  groWattSolarPlantAlert,
                                  groWattSolarPlantAlert.status === 1,
                                )
                                  ? `text-red`
                                  : `text-light`
                              }
                            >
                              {isOpen(
                                groWattSolarPlantAlert,
                                groWattSolarPlantAlert.status === 1,
                              )
                                ? `Active`
                                : `Resolved`}
                            </div>
                          </>,
                        ],
                      };
                    })}
                />
              </Col>
            </Row>
          </div>
        )}
      {serviceProvider === ESolarServiceProvider.POWERHUB &&
        powerHubSolarPlantAlerts.length > 0 && (
          <div className="bg-white">
            <Row className="mt-4 px-3">
              <Col>
                <DataTable
                  headers={[
                    { id: 1, columnName: "Alert Name" },
                    { id: 2, columnName: "DIN" },
                    { id: 3, columnName: "Start Time" },
                    { id: 4, columnName: "End Time" },
                    { id: 5, columnName: "Description" },
                    { id: 6, columnName: "Severity" },
                    { id: 7, columnName: "Status" },
                  ]}
                  data={powerHubSolarPlantAlerts
                    .slice(0, pageSize)
                    .map((powerhubAlert) => {
                      return {
                        data: [
                          <>{powerhubAlert.alertName}</>,
                          <>{powerhubAlert.din || "-"}</>,
                          <>
                            {powerhubAlert.startTime
                              ? moment
                                  .utc(powerhubAlert.startTime)
                                  .format("DD/MM/YY HH:mm:ss")
                              : "-"}
                          </>,
                          <>
                            {powerhubAlert.endTime
                              ? moment
                                  .utc(powerhubAlert.endTime)
                                  .format("DD/MM/YY HH:mm:ss")
                              : "-"}
                          </>,
                          <>{powerhubAlert.description || "-"}</>,
                          <>{powerhubAlert.severity || "-"}</>,
                          <>
                            <div
                              className={
                                powerhubAlert.isActive
                                  ? `text-red`
                                  : `text-light`
                              }
                            >
                              {powerhubAlert.isActive ? `Active` : `Resolved`}
                            </div>
                          </>,
                        ],
                      };
                    })}
                />
              </Col>
            </Row>
          </div>
        )}
      {showCommonTable && otherAlerts.length > 0 && (
        <div className="bg-white">
          <Row className="mt-4 px-3">
            <Col>
              <DataTable
                headers={[
                  { id: 1, columnName: "Alert" },
                  { id: 2, columnName: "Severity" },
                  { id: 3, columnName: "Device" },
                  { id: 4, columnName: "Start Time" },
                  { id: 5, columnName: "End Time" },
                  { id: 6, columnName: "Status" },
                ]}
                data={otherAlerts.slice(0, pageSize).map((alert) => {
                  const normalized = alert.normalized;

                  return {
                    data: [
                      <>{normalized?.name || "-"}</>,
                      <>
                        {normalized?.severity
                          ? severityLabels[normalized.severity]
                          : "-"}
                      </>,
                      <>{normalized?.device || "-"}</>,
                      <>{formatUtc(normalized?.startTime)}</>,
                      <>{formatUtc(normalized?.endTime)}</>,
                      <>
                        <div
                          className={
                            normalized?.open ? `text-red` : `text-light`
                          }
                        >
                          {normalized
                            ? normalized.open
                              ? `Active`
                              : `Resolved`
                            : "-"}
                        </div>
                      </>,
                    ],
                  };
                })}
              />
            </Col>
          </Row>
        </div>
      )}
      {goodWeSolarPlantAlerts.length === 0 &&
        vrmSolarPlantAlerts.length === 0 &&
        groWattSolarPlantAlerts.length === 0 &&
        powerHubSolarPlantAlerts.length === 0 && (
          <div className="container-dash d-flex align-items-center justify-content-center text-light mt-4 font-size-14">
            There are no error logs
          </div>
        )}
      {(vrmSolarPlantAlerts.length > pageSize ||
        goodWeSolarPlantAlerts.length > pageSize ||
        groWattSolarPlantAlerts.length > pageSize ||
        powerHubSolarPlantAlerts.length > pageSize) && (
        <Row
          className="justify-content-center cursor-pointer my-2"
          onClick={() => setPageSize(pageSize + 10)}
        >
          <Col className="col-auto">
            <Row className="justify-content-center">
              <Col className="col-auto text-center text-primary font-weight-500 font-size-11">
                Show More
              </Col>
            </Row>
            <Row className="justify-content-center">
              <Col className="col-auto text-center">
                <MaterialIcon
                  icon="stat_minus_1"
                  color="#1A3C84"
                  size={18}
                  weight={500}
                />
              </Col>
            </Row>
          </Col>
        </Row>
      )}
      <SiteErrorLogFilterPanel
        show={showSiteViewDashboardFilters}
        onClose={() => setShowSiteViewDashboardFilters(false)}
        updateFilters={updateFilters}
        filters={filters}
        zoneId={zoneId}
        alertOptions={alertOptions}
      />
      <SpinnerModal show={isFetching} />
    </div>
  );
};

export default SiteErrorLog;
